import { StateManager } from './managers/state-manager.js'
import { PreflightManager } from './managers/preflight-manager.js'
import { PageManager } from './managers/page-manager.js'
import { CalendarManager } from './managers/calendar-manager.js'
import { OnScreenBand } from './managers/on-screen-band.js'
import { OnScreenRibbon } from './managers/on-screen-ribbon.js'
import './schedule-calendar.component.less'

/**
 * The controller of the calendar page.
 *
 * The controller creates the managers and connects them to the template. Each
 * manager owns one concern, and the controller adds no logic of its own.
 */
class ScheduleCalendarController {
  constructor ($scope, $log, $timeout, $element, $state, toaster, SceneCollectionService) {
    'ngInject'

    this._$scope = $scope
    this._$state = $state
    this._toaster = toaster
    this._$log = $log
    this._$timeout = $timeout
    this._$element = $element
    this._SceneCollectionService = SceneCollectionService

    // CalendarManager owns the FullCalendar instance. The other managers use
    // this.calendarManager.calendar to reach it.
    this.stateManager = new StateManager(this, SceneCollectionService)
    this.preflightManager = new PreflightManager(this, SceneCollectionService)
    this.pageManager = new PageManager(this)
    this.calendarManager = new CalendarManager(this, SceneCollectionService)
    this.onScreenBand = new OnScreenBand(this)
    this.onScreenRibbon = new OnScreenRibbon(this)
  }

  $onInit () {
    this.pageManager.enter()
  }

  // ui-router puts the view into the document before it links the view. The
  // calendar element is therefore in the document here, and FullCalendar can
  // measure it.
  $postLink () {
    this.calendarManager.create(this._$element[0].querySelector('#schedule-calendar'))
  }

  $onDestroy () {
    this.pageManager.leave()
    this.calendarManager.destroy()
  }

  /** The close button in the toolbar. The calendar is a page, so this navigates. */
  closeCalendar () {
    this._$state.go('manager.scene', { id: this.scene && this.scene.id })
  }

  showPrevious () {
    this.calendarManager.showPrevious()
  }

  showNext () {
    this.calendarManager.showNext()
  }

  showToday () {
    this.calendarManager.showToday()
  }

  showView (viewType) {
    this.calendarManager.showView(viewType)
  }

  /** @param {string} direction - 'in' for finer slots, 'out' for coarser slots */
  zoom (direction) {
    this.calendarManager.slotZoom.cycleSlotDuration(direction)
  }

  /**
   * Open the content of a playlist from the inspector.
   *
   * This goes to the scene page, and opens that playlist for editing on
   * arrival.
   */
  openPlaylist (sceneCollectionId) {
    this._$state.go('manager.scene', {
      id: this.scene && this.scene.id,
      collection: sceneCollectionId
    })
  }

  editPanel (changes) {
    this.stateManager.editPanel(changes)
  }

  saveInspector (winsPeers) {
    this.stateManager.panelSave.saveInspector({ winsPeers })
  }

  deleteInterval (interval) {
    this.stateManager.deleteInterval(interval)
  }

  closeInspector () {
    this.stateManager.closeInspector()
  }

  openPreflight () {
    return this.preflightManager.openPreflight()
  }

  closePreflight () {
    this.preflightManager.closePreflight()
  }

  openPreflightRow (interval) {
    this.preflightManager.openPreflightRow(interval)
  }

  /** Point at the interval of a report row in the grid. Null removes the outline. */
  highlightInterval (intervalId) {
    this.calendarManager.highlightInterval(intervalId)
  }

  inspect (calendarEvent) {
    this.stateManager.inspect(calendarEvent)
  }

  followSavedTimes (calendarEvent, updatedAt) {
    this.stateManager.followSavedTimes(calendarEvent, updatedAt)
  }

  inspectNew (start, end, role) {
    this.stateManager.inspectNew(start, end, role)
  }

  punchInTakeover () {
    this.stateManager.punchInTakeover()
  }

  followCalendarEvents () {
    this.stateManager.followCalendarEvents()
  }

  followReconnect () {
    this.stateManager.panelSave.followReconnect()
  }

  isSavingInterval (intervalId) {
    return this.stateManager.panelSave.isSavingInterval(intervalId)
  }
}

export default {
  bindings: {
    // The route resolves this. It is the scene object, not an id. ui-router
    // binds it one time, and a new scene id mounts a new component.
    scene: '<'
  },
  controller: ScheduleCalendarController,
  template: require('./schedule-calendar.tpl.html').default
}
